/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, type ReactNode } from 'react';

interface CommonParamsContextType {
  seasonYear: string | null;
  circuitId: string | null;
  startDate: string | null;
  source: string | null;
  setSeasonYear: (value: string | null) => void;
  setCircuitId: (value: string | null) => void;
  setStartDate: (value: string | null) => void;
  setSource: (value: string | null) => void;
  clearAll: () => void;
}

const CommonParamsContext = createContext<CommonParamsContextType | undefined>(undefined);

const STORAGE_KEYS = {
  SEASON_YEAR: 'api-docs-common-season-year',
  CIRCUIT_ID: 'api-docs-common-circuit-id',
  START_DATE: 'api-docs-common-start-date',
  SOURCE: 'api-docs-common-source',
};

export const CommonParamsProvider = ({ children }: { children: ReactNode }) => {
  const [seasonYear, setSeasonYearState] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.SEASON_YEAR);
  });
  const [circuitId, setCircuitIdState] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.CIRCUIT_ID);
  });
  const [startDate, setStartDateState] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.START_DATE);
  });
  const [source, setSourceState] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.SOURCE);
  });

  const setSeasonYear = (value: string | null) => {
    setSeasonYearState(value);
    if (value) {
      localStorage.setItem(STORAGE_KEYS.SEASON_YEAR, value);
    } else {
      localStorage.removeItem(STORAGE_KEYS.SEASON_YEAR);
    }
  };

  const setCircuitId = (value: string | null) => {
    setCircuitIdState(value);
    if (value) {
      localStorage.setItem(STORAGE_KEYS.CIRCUIT_ID, value);
    } else {
      localStorage.removeItem(STORAGE_KEYS.CIRCUIT_ID);
    }
  };

  const setStartDate = (value: string | null) => {
    setStartDateState(value);
    if (value) {
      localStorage.setItem(STORAGE_KEYS.START_DATE, value);
    } else {
      localStorage.removeItem(STORAGE_KEYS.START_DATE);
    }
  };

  const setSource = (value: string | null) => {
    setSourceState(value);
    if (value) {
      localStorage.setItem(STORAGE_KEYS.SOURCE, value);
    } else {
      localStorage.removeItem(STORAGE_KEYS.SOURCE);
    }
  };

  const clearAll = () => {
    setSeasonYearState(null);
    setCircuitIdState(null);
    setStartDateState(null);
    setSourceState(null);
    localStorage.removeItem(STORAGE_KEYS.SEASON_YEAR);
    localStorage.removeItem(STORAGE_KEYS.CIRCUIT_ID);
    localStorage.removeItem(STORAGE_KEYS.START_DATE);
    localStorage.removeItem(STORAGE_KEYS.SOURCE);
  };

  return (
    <CommonParamsContext.Provider
      value={{
        seasonYear,
        circuitId,
        startDate,
        source,
        setSeasonYear,
        setCircuitId,
        setStartDate,
        setSource,
        clearAll,
      }}
    >
      {children}
    </CommonParamsContext.Provider>
  );
};

export const useCommonParams = () => {
  const context = useContext(CommonParamsContext);
  if (context === undefined) {
    throw new Error('useCommonParams must be used within CommonParamsProvider');
  }
  return context;
};

